/**
 * useKeyboardNavigation
 *
 * Unified keyboard navigation for ArTable and Form contexts, driven by the
 * shared keybinding registry (`keybindings.ts` is the source of truth for which
 * key does what; this hook owns execution). Uses React's native onKeyDown, no
 * global KeyboardManager, and stays zero-subscription: it reads Valtio via
 * `snapshot()` at event time and never calls `useSnapshot`, so it never
 * re-renders when cells change.
 *
 * Table:
 * - ArrowUp/Down: move cell vertically.
 * - ArrowLeft/Right: boundary-aware — move the text caret, and cross to the
 *   adjacent cell only when the caret sits at the text edge (non-text cells cross
 *   immediately). No Shift required.
 * - Enter: commit + move down. Shift+Enter: newline in a text cell.
 * - Tab/Shift+Tab: commit + move right/left.
 * - ⌘/Ctrl+Shift+Enter: insert row below the focused cell.
 * - ⌘/Ctrl+Shift+C: copy the focused row, or the selection holding it. The chord
 *   is a keydown with no `clipboardData`, so it cannot reach the native copy
 *   handler and is dispatched to `onCopyRows` instead.
 *
 * Form:
 * - Enter: commit + next field. Shift+Enter: newline in a multi-line field.
 * - Shift+ArrowUp/Down: previous/next field.
 */

import { useCallback, useMemo } from 'react';
import { snapshot } from 'valtio';
import { getNextGridCoord, type GridMove } from './gridNavigation';
import {
  buildCellId,
  caretAtEnd,
  caretAtStart,
  focusCellByIdWithRetry,
  isMultilineEditable,
  isOverlayOpen,
  parseCellIdFromElement,
} from './focus';
import { keybindingRegistry } from './keybindingRegistry';
import { matchChord } from './matchChord';
import type { ArColumnDefinition, ArRowData, ArRowId, ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { CollectionState, DocumentState } from '@/store';
import { resolveSelectionScope } from '../components/ArTable/selection';
import type { ArTableUIState } from '../components/ArTable/state/createArTableState';
import type { Actions } from '../components/ArTable/actions/useArTableActions';

type TableConfig = {
  mode: 'table';
  uiState: ArTableUIState;
  schemaState: DocumentState<ArTableType>;
  rowsState: CollectionState<ArRowData>;
  actions: Actions;
  onRequestDeleteRows: (rowIds: ArRowId[]) => void;
  /**
   * ⌘⇧C. Supplied by `ArTableBody` for the same reason `onRequestDeleteRows` is:
   * the row copy needs `fieldTypes` and the clipboard module, and this hook holds
   * neither.
   */
  onCopyRows: () => void;
};

type FormConfig = {
  mode: 'form';
};

type UseKeyboardNavigationParams = {
  disabled: boolean;
} & (TableConfig | FormConfig);

type UseKeyboardNavigationReturn = {
  onKeyDown: (e: React.KeyboardEvent) => void;
};

const TABLE_MOVE_BY_ID: Record<string, GridMove> = {
  'table.moveUp': 'up',
  'table.moveDown': 'down',
  'table.moveLeft': 'shiftLeft',
  'table.moveRight': 'shiftRight',
  'table.next': 'tab',
  'table.prev': 'shiftTab',
  'table.commitDown': 'down',
};

const FORM_DIR_BY_ID: Record<string, 1 | -1> = {
  'form.commitNext': 1,
  'form.nextField': 1,
  'form.prevField': -1,
};

const isHiddenColumn = (col: ArColumnDefinition): boolean => {
  const v = col.visibilityTypes;
  if (!v || v.length === 0) return false;
  return v.includes('hidden');
};

const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

export function useKeyboardNavigation(
  params: UseKeyboardNavigationParams
): UseKeyboardNavigationReturn {
  const { disabled } = params;

  const colMeta = useMemo(() => {
    if (params.mode !== 'table') return {};
    const cols = params.schemaState.data?.columns ?? [];
    const formulaTypeIds = new Set((params.schemaState.data?.formulaSequence ?? []).map((e) => e.typeId));
    const byId: Record<string, { editable: boolean; formula: boolean; hidden: boolean }> = {};
    for (const c of cols) {
      byId[c.typeId] = {
        editable: (c as any)?.editable !== false,
        formula: formulaTypeIds.has(c.typeId),
        hidden: isHiddenColumn(c),
      };
    }
    return byId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mode-gated: only recompute from schema when in table mode
  }, [params.mode === 'table' ? params.schemaState.data?.columns : null, params.mode === 'table' ? params.schemaState.data?.formulaSequence : null]);

  const isNavigable = useCallback((rowId: ArRowId, colId: TypeId): boolean => {
    if (params.mode !== 'table') return false;
    const meta = colMeta[colId];
    if (!meta || meta.hidden || !meta.editable || meta.formula) return false;

    const row = params.rowsState.docs.get(rowId)?.data;
    if (!row) return false;
    if ((row as any)?.locked === true) return false;
    const cellLocked = (row.cells as any)?.[colId]?.locked === true;
    if (cellLocked) return false;
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mode-gated: only recompute from row docs when in table mode
  }, [colMeta, params.mode === 'table' ? params.rowsState.docs : null]);

  const handleTableKeyDown = useCallback((e: React.KeyboardEvent, activeEl: HTMLElement): boolean => {
    if (params.mode !== 'table') return false;
    const { uiState, schemaState, rowsState, actions, onRequestDeleteRows, onCopyRows } = params;

    if (schemaState.loading || rowsState.loading) return false;

    const binding = keybindingRegistry
      .byContext('table')
      .find((b) => b.chords.some((c) => matchChord(e, c)));
    if (!binding) return false;

    if (binding.id === 'table.addRow') {
      const focused = snapshot(uiState).cellFocus.focusedCell;
      if (!focused) return false;
      e.preventDefault();
      e.stopPropagation();
      void actions.addRow(undefined, { afterRowId: focused.rowId as ArRowId });
      return true;
    }

    if (binding.id === 'table.copyRows') {
      e.preventDefault();
      e.stopPropagation();
      onCopyRows();
      return true;
    }

    if (binding.id === 'table.deleteRow') {
      const uiSnap = snapshot(uiState);
      /**
       * Scoped, so ⌘⌫ cannot delete a ticked row the current split, filter or
       * view has taken off screen (SYD-2386).
       *
       * The ticks are the target even while the selection scope is `'all'`
       * (SYD-2386, S13). Deliberate: the whole-table delete is reachable only
       * from the Actions menu, where the escalation was made and where the
       * scope-aware confirmation lives. The confirmation this key raises counts
       * exactly the rows it hands over, so no path overstates its own reach.
       */
      const { selectedVisibleRowIds: selected } = resolveSelectionScope({
        selectableRowIds: uiSnap.selectableRowIds,
        selectedRows: uiSnap.selection.selectedRows,
        scope: uiSnap.selection.scope,
      });
      const focusedRowId = uiSnap.cellFocus.focusedCell?.rowId as ArRowId | undefined;
      const rowIds = selected.length > 0 ? selected : focusedRowId ? [focusedRowId] : [];
      if (rowIds.length === 0) return false;
      e.preventDefault();
      e.stopPropagation();
      onRequestDeleteRows(rowIds);
      return true;
    }

    const move = TABLE_MOVE_BY_ID[binding.id];
    if (!move) return false;

    if (binding.id === 'table.commitDown' && isMultilineEditable(activeEl)) return false;
    if (binding.id === 'table.moveLeft' && !caretAtStart(activeEl)) return false;
    if (binding.id === 'table.moveRight' && !caretAtEnd(activeEl)) return false;

    const uiSnap = snapshot(uiState);
    const renderedRowIds = (uiSnap.renderedRowIds ?? []) as ArRowId[];
    const colIds = (uiSnap.renderedColIds ?? []) as TypeId[];
    if (renderedRowIds.length === 0 || colIds.length === 0) return false;

    const current = (() => {
      const focused = uiSnap.cellFocus.focusedCell;
      if (focused) {
        return { rowId: focused.rowId as ArRowId, colId: focused.columnId as TypeId };
      }
      const parsed = parseCellIdFromElement(activeEl);
      if (parsed) {
        uiState.cellFocus.focusedCell = { rowId: parsed.rowId, columnId: parsed.colId };
        return { rowId: parsed.rowId, colId: parsed.colId };
      }
      return null;
    })();

    if (!current) return false;

    const next = getNextGridCoord({
      rows: renderedRowIds,
      cols: colIds,
      current,
      move,
      wrap: true,
      isNavigable,
    });

    if (!next) return false;

    e.preventDefault();
    e.stopPropagation();

    uiState.cellFocus.focusedCell = { rowId: next.rowId, columnId: next.colId };
    focusCellByIdWithRetry(buildCellId(next.colId, next.rowId), 3);
    return true;
  }, [params, isNavigable]);

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent, activeEl: HTMLElement): boolean => {
    const binding = keybindingRegistry
      .byContext('form')
      .find((b) => b.chords.some((c) => matchChord(e, c)));
    if (!binding) return false;

    const dir = FORM_DIR_BY_ID[binding.id];
    if (!dir) return false;

    if (binding.id === 'form.commitNext' && isMultilineEditable(activeEl)) return false;

    const root = e.currentTarget as HTMLElement;
    if (!root) return false;

    const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!focusables.length) return false;

    const list = Array.from(focusables);
    const currentIdx = list.indexOf(activeEl);
    if (currentIdx === -1) return false;

    const nextIdx = currentIdx + dir;
    if (nextIdx < 0 || nextIdx >= list.length) return false;

    e.preventDefault();
    e.stopPropagation();

    const nextEl = list[nextIdx];
    nextEl.focus();
    nextEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    return true;
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (disabled) return;
    if (e.defaultPrevented) return;
    if (e.nativeEvent.isComposing) return;

    const activeEl = document.activeElement;
    if (!(activeEl instanceof HTMLElement)) return;
    if (isOverlayOpen(activeEl)) return;

    if (params.mode === 'table') {
      handleTableKeyDown(e, activeEl);
    } else {
      handleFormKeyDown(e, activeEl);
    }
  }, [disabled, params.mode, handleTableKeyDown, handleFormKeyDown]);

  return { onKeyDown };
}
